import {
  CompanyBrandingData,
  type CompanyBrandAssetsResponse,
  type OnBoardingBrandingData,
  type OnBoardingBrandingResponse,
} from '@customTypes/CompanyBranding';
import { DEFAULT_BRANDING } from '@constants/branding';
import ApiService from './ApiService';

class CompanyBrandingService {
  /**
   * Get the branding data for the company.
   * Returns DEFAULT_BRANDING when the company has no branding yet (404).
   */
  public async getBranding(companyName: string): Promise<CompanyBrandingData> {
    if (!companyName || companyName.trim() === '') {
      throw new Error('Company name is required');
    }

    const { success, data, error, status } =
      await ApiService.get<CompanyBrandingData>(
        `/company/${companyName.trim()}/branding`,
      );

    if (success) {
      return data || ({} as CompanyBrandingData);
    }
    if (status === 404) {
      return DEFAULT_BRANDING;
    }
    throw new Error(error || 'Failed to get branding');
  }

  /**
   * Save the branding data for the company
   * @param brandingData - The branding data to save
   * @returns BrandingData
   */
  public async saveOnBoardingBranding(
    companyName: string,
    brandingData: FormData,
  ): Promise<OnBoardingBrandingData> {
    const { success, data, error } = await ApiService.postForm<
      FormData,
      OnBoardingBrandingData
    >(`/onboarding/company/${companyName}/branding`, brandingData);

    if (success) {
      return data || ({} as OnBoardingBrandingData);
    } else {
      throw new Error(error || 'Failed to save branding');
    }
  }

  /**
   * Get AI branding data for onboarding
   * @param companyName - The company name
   * @returns BrandingData
   */
  public async getOnboardingBranding(
    companyName: string,
  ): Promise<OnBoardingBrandingResponse> {
    const { success, data, error } =
      await ApiService.get<OnBoardingBrandingResponse>(
        `/onboarding/company/${companyName}/branding`,
      );

    if (success) {
      return data || ({} as OnBoardingBrandingResponse);
    } else {
      throw new Error(error || 'Failed to get onboarding branding');
    }
  }

  /**
   * Save the branding data for the company
   * @param brandingData - The branding data to save
   * @returns BrandingData
   */
  public async saveBranding(
    companyName: string,
    brandingData: Partial<CompanyBrandingData>,
  ): Promise<CompanyBrandingData> {
    const { success, data, error } = await ApiService.postForm<
      Partial<CompanyBrandingData>,
      CompanyBrandingData
    >(`/company/${companyName}/branding`, brandingData);

    if (success) {
      return data || ({} as CompanyBrandingData);
    } else {
      throw new Error(error || 'Failed to save branding');
    }
  }

  public async getBrandAssets(
    companyName: string,
  ): Promise<CompanyBrandAssetsResponse> {
    const { success, data, error } =
      await ApiService.get<CompanyBrandAssetsResponse>(
        `/company/${companyName.trim()}/branding/assets`,
      );

    if (success) {
      return (
        data || {
          logos: [],
          banner_url: '',
          screenshot_url: '',
          description: '',
          conventions: '',
          source: '',
        }
      );
    }

    throw new Error(error || 'Failed to get brand assets');
  }
}

export default new CompanyBrandingService();
